import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, UserPlus, Trash2, Search, Calendar, Plus, Download, FileText,
  Sparkles, RefreshCw, CheckCircle2, Clock, X, Eye, Printer, Mail,
  ChevronDown, ChevronUp, Loader2
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const DUTY_GROUPS = [
  { key: 'lighting_of_candle', label: 'Lighting of Candle',         gender: 'Any',    color: 'from-amber-500 to-yellow-500' },
  { key: 'tithe_box',          label: 'Tithe Box',                  gender: 'Any',    color: 'from-green-600 to-emerald-600' },
  { key: 'male_entrance',      label: 'Male Entrance',              gender: 'Male',   color: 'from-blue-500 to-indigo-500' },
  { key: 'female_entrance',    label: 'Female Entrance',            gender: 'Female', color: 'from-pink-500 to-rose-500' },
  { key: 'collection_male',    label: 'Collection – Male',          gender: 'Male',   color: 'from-emerald-500 to-teal-500' },
  { key: 'collection_female',  label: 'Collection – Female',        gender: 'Female', color: 'from-violet-500 to-purple-500' },
  { key: 'male_sitting',       label: 'Male Sitting Arrangement',   gender: 'Male',   color: 'from-sky-500 to-blue-500' },
  { key: 'female_sitting',     label: 'Female Sitting Arrangement', gender: 'Female', color: 'from-fuchsia-500 to-pink-500' },
];

const nextSunday = () => {
  const d = new Date();
  const diff = (7 - d.getDay()) % 7 || 7; // days until next Sunday (never today)
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
};

const formatSundayDisplay = (iso) => {
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

const UsherRotaSection = () => {
  const [innerTab, setInnerTab] = useState('rotas'); // 'team' | 'rotas'
  const [team, setTeam] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [loadingRotas, setLoadingRotas] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [autoBusy, setAutoBusy] = useState(false);
  const [selectedRotaId, setSelectedRotaId] = useState(null);
  const [createDate, setCreateDate] = useState(nextSunday());

  const loadTeam = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const res = await axios.get(`${API}/usher-rota/team`);
      setTeam(res.data || []);
    } catch (e) { console.error('loadTeam error', e); }
    setLoadingTeam(false);
  }, []);

  const loadAllMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/members`);
      setAllMembers(res.data || []);
    } catch (e) { console.error('loadAllMembers error', e); }
  }, []);

  const loadRotas = useCallback(async () => {
    setLoadingRotas(true);
    try {
      const res = await axios.get(`${API}/usher-rota/rotas`, { params: { month } });
      setRotas(res.data || []);
    } catch (e) { console.error('loadRotas error', e); }
    setLoadingRotas(false);
  }, [month]);

  useEffect(() => { loadTeam(); loadAllMembers(); }, [loadTeam, loadAllMembers]);
  useEffect(() => { loadRotas(); }, [loadRotas]);

  // ---------------- Team management ----------------
  const teamMemberIds = useMemo(() => new Set(team.map(t => t.member_id)), [team]);
  const memberPickerList = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return (allMembers || [])
      .filter(m => !teamMemberIds.has(m.id))
      .filter(m => !q
        || (m.full_name || '').toLowerCase().includes(q)
        || (m.phone || '').toLowerCase().includes(q)
        || (m.member_number || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [allMembers, teamMemberIds, memberSearch]);

  const addTeamMember = async (memberId) => {
    try {
      await axios.post(`${API}/usher-rota/team`, { member_id: memberId });
      setMemberSearch('');
      setShowMemberPicker(false);
      await loadTeam();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to add to usher team');
    }
  };

  const toggleActive = async (entry) => {
    try {
      await axios.put(`${API}/usher-rota/team/${entry.id}`, { active: !entry.active });
      setTeam(prev => prev.map(x => x.id === entry.id ? { ...x, active: !entry.active } : x));
    } catch (e) { console.error(e); }
  };

  const saveEligibleDuties = async (entry, newDuties) => {
    try {
      await axios.put(`${API}/usher-rota/team/${entry.id}`, { eligible_duties: newDuties });
      setTeam(prev => prev.map(x => x.id === entry.id ? { ...x, eligible_duties: newDuties } : x));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save eligible duties');
    }
  };

  const removeTeamMember = async (entryId) => {
    if (!window.confirm('Remove this member from the Usher Team?')) return;
    try {
      await axios.delete(`${API}/usher-rota/team/${entryId}`);
      setTeam(prev => prev.filter(x => x.id !== entryId));
    } catch (e) { console.error(e); }
  };

  // ---------------- Rotas ----------------
  const createBlankRota = async () => {
    if (!createDate) return;
    try {
      const res = await axios.post(`${API}/usher-rota/rotas`, { date: createDate, notes: '' });
      setSelectedRotaId(res.data.id);
      await loadRotas();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create rota');
    }
  };

  const deleteRota = async (id) => {
    if (!window.confirm('Delete this rota?')) return;
    try {
      await axios.delete(`${API}/usher-rota/rotas/${id}`);
      setRotas(prev => prev.filter(r => r.id !== id));
      if (selectedRotaId === id) setSelectedRotaId(null);
    } catch (e) { console.error(e); }
  };

  const togglePublish = async (rota) => {
    try {
      const ep = rota.status === 'published' ? 'unpublish' : 'publish';
      const res = await axios.post(`${API}/usher-rota/rotas/${rota.id}/${ep}`);
      if (ep === 'publish' && res.data?.emails) {
        const e = res.data.emails;
        alert(
          `✅ Rota published.\n\n` +
          `📧 Duty emails:\n` +
          `   Sent: ${e.sent}\n` +
          `   Skipped (no email on file): ${e.skipped}\n` +
          `   Failed: ${e.failed}\n` +
          `   Total assignees: ${e.total_assignees}`
        );
      }
      await loadRotas();
    } catch (e) { console.error(e); }
  };

  const resendDutyEmails = async (rota) => {
    if (!window.confirm(`Re-send duty emails (with .ics calendar invite) to every assigned usher for ${rota.date}?`)) return;
    try {
      const res = await axios.post(`${API}/usher-rota/rotas/${rota.id}/resend-duty-emails`);
      const e = res.data.emails || {};
      alert(`📧 Duty emails sent: ${e.sent}\nSkipped: ${e.skipped}\nFailed: ${e.failed}\nTotal assignees: ${e.total_assignees}`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to resend duty emails');
    }
  };

  const duplicateRota = async (rota) => {
    const newDate = window.prompt('Duplicate to which Sunday? (YYYY-MM-DD)', nextSunday());
    if (!newDate) return;
    try {
      await axios.post(`${API}/usher-rota/rotas/${rota.id}/duplicate`, null, { params: { new_date: newDate } });
      await loadRotas();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to duplicate'); }
  };

  const autoGenerateSingle = async () => {
    const target = selectedRotaId ? rotas.find(r => r.id === selectedRotaId)?.date : createDate;
    if (!target) { alert('Pick a date first'); return; }
    setAutoBusy(true);
    try {
      await axios.post(`${API}/usher-rota/auto-generate`, null, {
        params: { scope: 'single', date: target, overwrite: true },
      });
      await loadRotas();
    } catch (e) { alert(e.response?.data?.detail || 'Auto-generate failed'); }
    setAutoBusy(false);
  };

  const autoGenerateMonth = async () => {
    if (!window.confirm(`Auto-generate every Sunday in ${month}? Existing rotas will be kept unless you confirm overwrite.`)) return;
    const overwrite = window.confirm('Also overwrite any existing rotas this month? Click OK to overwrite, Cancel to skip existing.');
    setAutoBusy(true);
    try {
      const res = await axios.post(`${API}/usher-rota/auto-generate`, null, {
        params: { scope: 'month', month, overwrite },
      });
      alert(`✅ Generated ${res.data.count} rota(s) for ${month}`);
      await loadRotas();
    } catch (e) { alert(e.response?.data?.detail || 'Auto-generate failed'); }
    setAutoBusy(false);
  };

  const downloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const selectedRota = rotas.find(r => r.id === selectedRotaId) || null;

  // ---------------- Render ----------------
  return (
    <div className="space-y-5" data-testid="usher-rota-section">
      {/* Inner nav */}
      <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-100 flex gap-2">
        <button
          onClick={() => setInnerTab('rotas')}
          data-testid="usher-rota-tab-rotas"
          className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            innerTab === 'rotas' ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow'
                                 : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
        >
          <Calendar className="w-4 h-4" /> Sunday Rotas
        </button>
        <button
          onClick={() => setInnerTab('team')}
          data-testid="usher-rota-tab-team"
          className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            innerTab === 'team' ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-violet-950 shadow'
                                : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
        >
          <Users className="w-4 h-4" /> Usher Team ({team.length})
        </button>
      </div>

      <AnimatePresence mode="wait">
        {innerTab === 'team' ? (
          <motion.div key="team" initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-10}}
            className="bg-white rounded-3xl p-5 shadow-lg border border-slate-100">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Usher Team Roster</h3>
                <p className="text-sm text-slate-500">Add members from the church members list. Only active members are used in rota generation.</p>
              </div>
              <button onClick={() => setShowMemberPicker(v => !v)} data-testid="usher-add-team-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-violet-950 text-sm font-bold shadow hover:from-amber-600 hover:to-yellow-600">
                <UserPlus className="w-4 h-4" /> Add from Members
              </button>
            </div>

            {showMemberPicker && (
              <div className="mb-4 p-4 rounded-2xl border border-amber-200 bg-amber-50/50">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" placeholder="Search members by name, phone, or M-number…"
                    value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                    data-testid="usher-member-search"
                    className="pl-9 pr-4 py-2.5 w-full border border-slate-200 rounded-xl text-sm outline-none focus:border-amber-400" />
                </div>
                <div className="max-h-72 overflow-y-auto space-y-2">
                  {memberPickerList.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">No members match.</p>
                  )}
                  {memberPickerList.map(m => (
                    <button key={m.id} onClick={() => addTeamMember(m.id)}
                      data-testid={`usher-pick-${m.id}`}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-xl bg-white border border-slate-100 hover:border-amber-300 hover:bg-amber-50/50 text-left transition-all">
                      <div>
                        <p className="font-semibold text-slate-800">{m.full_name}</p>
                        <p className="text-xs text-slate-500">{m.member_number} · {m.phone || 'no phone'} · {m.gender || '—'}</p>
                      </div>
                      <Plus className="w-4 h-4 text-amber-600" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loadingTeam ? (
              <p className="text-sm text-slate-500 text-center py-8">Loading team…</p>
            ) : team.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No ushers yet</p>
                <p className="text-sm">Click "Add from Members" to build your team.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3" data-testid="usher-team-list">
                {team.map(t => (
                  <UsherTeamRow
                    key={t.id}
                    entry={t}
                    onToggleActive={() => toggleActive(t)}
                    onRemove={() => removeTeamMember(t.id)}
                    onSaveEligibleDuties={(newDuties) => saveEligibleDuties(t, newDuties)}
                  />
                ))}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div key="rotas" initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-10}} className="space-y-5">
            {/* Top controls */}
            <div className="bg-white rounded-3xl p-5 shadow-lg border border-slate-100 space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-violet-600" /> Month
                </label>
                <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                  data-testid="usher-rota-month" className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-violet-400" />
                <button onClick={loadRotas} data-testid="usher-rota-refresh"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm hover:bg-slate-200">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
                <div className="ml-auto flex gap-2 flex-wrap">
                  <button onClick={autoGenerateMonth} disabled={autoBusy || team.filter(t=>t.active).length === 0}
                    data-testid="usher-auto-month-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-bold shadow hover:from-violet-700 hover:to-purple-700 disabled:opacity-50">
                    {autoBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
                    Auto-Generate Month
                  </button>
                  <a href={`${API}/usher-rota/month/${month}/pdf`} target="_blank" rel="noopener noreferrer"
                    data-testid="usher-month-pdf"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-violet-950 text-sm font-bold shadow hover:bg-amber-600">
                    <FileText className="w-4 h-4" /> Month PDF
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap pt-3 border-t border-slate-100">
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-emerald-600" /> Create rota for
                </label>
                <input type="date" value={createDate} onChange={e => setCreateDate(e.target.value)}
                  data-testid="usher-rota-new-date" className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-emerald-400" />
                <button onClick={createBlankRota} data-testid="usher-create-blank"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600">
                  <Plus className="w-4 h-4" /> New Rota
                </button>
              </div>
            </div>

            {/* Rota cards */}
            {loadingRotas ? (
              <p className="text-sm text-slate-500 text-center py-8">Loading rotas…</p>
            ) : rotas.length === 0 ? (
              <div className="bg-white rounded-3xl p-8 border border-slate-100 text-center text-slate-500">
                <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="font-medium">No rotas for {month}</p>
                <p className="text-sm">Create one above or run Auto-Generate Month.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="usher-rotas-list">
                {rotas.map(r => (
                  <RotaCard
                    key={r.id}
                    rota={r}
                    team={team}
                    isOpen={selectedRotaId === r.id}
                    onToggleOpen={() => setSelectedRotaId(selectedRotaId === r.id ? null : r.id)}
                    onChange={loadRotas}
                    onDelete={() => deleteRota(r.id)}
                    onTogglePublish={() => togglePublish(r)}
                    onDuplicate={() => duplicateRota(r)}
                    onResendEmails={() => resendDutyEmails(r)}
                    onAutoGenerate={autoGenerateSingle}
                    autoBusy={autoBusy && selectedRota?.id === r.id}
                    onDownloadPdf={() => downloadFile(`${API}/usher-rota/rotas/${r.id}/pdf`, `ushers-rota-${r.date}.pdf`)}
                    onDownloadExcel={() => downloadFile(`${API}/usher-rota/rotas/${r.id}/excel`, `ushers-rota-${r.date}.xlsx`)}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


// =======================================================================
// RotaCard — single Sunday rota with inline editing
// =======================================================================
const RotaCard = ({
  rota, team, isOpen, onToggleOpen, onChange, onDelete, onTogglePublish,
  onDuplicate, onResendEmails, onAutoGenerate, autoBusy,
  onDownloadPdf, onDownloadExcel,
}) => {
  const [assignments, setAssignments] = useState(rota.assignments || {});
  const [notes, setNotes] = useState(rota.notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setAssignments(rota.assignments || {}); setNotes(rota.notes || ''); }, [rota]);

  const activeTeam = useMemo(() => team.filter(t => t.active), [team]);

  const isPublished = rota.status === 'published';

  const countAssigned = useMemo(() => {
    return DUTY_GROUPS.reduce((sum, g) => sum + (assignments[g.key]?.length || 0), 0);
  }, [assignments]);

  const toggleAssign = (dutyKey, memberId) => {
    setAssignments(prev => {
      const current = prev[dutyKey] || [];
      const next = current.includes(memberId)
        ? current.filter(x => x !== memberId)
        : [...current, memberId];
      return { ...prev, [dutyKey]: next };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/usher-rota/rotas/${rota.id}`, { notes, assignments });
      await onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className={`rounded-3xl border shadow-sm overflow-hidden transition-all ${
      isPublished ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'
    }`} data-testid={`usher-rota-card-${rota.id}`}>
      {/* Header */}
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onToggleOpen}
          className="flex-1 flex items-center gap-3 min-w-0 text-left"
          data-testid={`usher-rota-toggle-${rota.id}`}>
          <div className={`p-2.5 rounded-xl ${isPublished ? 'bg-emerald-500' : 'bg-violet-500'} text-white shrink-0`}>
            {isPublished ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-slate-800 truncate">{formatSundayDisplay(rota.date)}</p>
            <p className="text-xs text-slate-500">
              {countAssigned} assignment{countAssigned === 1 ? '' : 's'} ·
              <span className={`ml-1 font-semibold ${isPublished ? 'text-emerald-600' : 'text-amber-600'}`}>
                {isPublished ? 'PUBLISHED' : 'DRAFT'}
              </span>
            </p>
          </div>
          {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400"/> : <ChevronDown className="w-4 h-4 text-slate-400"/>}
        </button>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={onDownloadPdf} data-testid={`usher-rota-pdf-${rota.id}`}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-xs font-semibold hover:bg-rose-100" title="Download PDF">
            <FileText className="w-3.5 h-3.5" /> PDF
          </button>
          <button onClick={onDownloadExcel} data-testid={`usher-rota-excel-${rota.id}`}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100" title="Download Excel">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
          <button onClick={onTogglePublish} data-testid={`usher-rota-publish-${rota.id}`}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold ${
              isPublished ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}>
            {isPublished ? 'Unpublish' : 'Publish'}
          </button>
          {isPublished && (
            <button onClick={onResendEmails} data-testid={`usher-rota-resend-${rota.id}`}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100" title="Resend duty emails with calendar invite">
              <Mail className="w-3.5 h-3.5" /> Email Duties
            </button>
          )}
          <button onClick={onDuplicate} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Duplicate">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={onDelete} data-testid={`usher-rota-delete-${rota.id}`}
            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded editor */}
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={onAutoGenerate} disabled={autoBusy || activeTeam.length === 0}
              data-testid={`usher-rota-autogen-${rota.id}`}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-bold shadow hover:from-violet-700 hover:to-purple-700 disabled:opacity-50">
              {autoBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
              Auto-generate this Sunday
            </button>
            <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200">
              <Printer className="w-4 h-4" /> Print
            </button>
            <button onClick={save} disabled={saving}
              data-testid={`usher-rota-save-${rota.id}`}
              className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-violet-950 text-sm font-bold hover:bg-amber-600 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
              Save Changes
            </button>
          </div>

          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Notes / Instructions (optional)"
            data-testid={`usher-rota-notes-${rota.id}`}
            className="w-full p-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-violet-400 min-h-[70px]" />

          <div className="grid md:grid-cols-2 gap-3">
            {DUTY_GROUPS.map(dg => (
              <DutyCard key={dg.key} dg={dg}
                team={activeTeam}
                assigned={assignments[dg.key] || []}
                onToggle={(mid) => toggleAssign(dg.key, mid)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// =======================================================================
// DutyCard — one of the 7 duty groups with member toggler
// =======================================================================
const DutyCard = ({ dg, team, assigned, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const [override, setOverride] = useState(false);

  const eligible = useMemo(() => {
    const isEligibleForThisDuty = (t) => {
      const list = t.eligible_duties || [];
      return list.length === 0 || list.includes(dg.key);
    };
    if (override) return team;
    const genderMatches = (t) =>
      dg.gender === 'Any' || (t.gender || '').toLowerCase() === dg.gender.toLowerCase();
    return team.filter(t => genderMatches(t) && isEligibleForThisDuty(t));
  }, [team, dg.gender, dg.key, override]);

  const byId = useMemo(() => Object.fromEntries(team.map(t => [t.id, t])), [team]);

  return (
    <div className={`rounded-2xl p-3 border border-slate-200 bg-white`}>
      <div className={`rounded-xl p-3 mb-2 bg-gradient-to-r ${dg.color} text-white flex items-center justify-between`}>
        <div>
          <p className="font-bold text-sm">{dg.label}</p>
          <p className="text-xs opacity-90">{dg.gender === 'Any' ? 'Any gender' : `${dg.gender} ushers`}</p>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-white/25 text-xs font-bold">{assigned.length}</span>
      </div>

      {/* Current assignees */}
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[24px]">
        {assigned.length === 0 ? (
          <span className="text-xs italic text-slate-400">— unassigned —</span>
        ) : assigned.map(mid => (
          <button key={mid} onClick={() => onToggle(mid)}
            data-testid={`duty-${dg.key}-assigned-${mid}`}
            className="px-2 py-1 rounded-full bg-violet-100 text-violet-800 text-xs font-semibold flex items-center gap-1 hover:bg-red-100 hover:text-red-700 transition-colors">
            {byId[mid]?.full_name || '(unknown)'} <X className="w-3 h-3" />
          </button>
        ))}
      </div>

      <button onClick={() => setExpanded(v => !v)} data-testid={`duty-${dg.key}-picker-toggle`}
        className="text-xs font-semibold text-violet-700 hover:underline flex items-center gap-1">
        {expanded ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
        {expanded ? 'Hide picker' : 'Assign ushers'}
      </button>

      {expanded && (
        <div className="mt-2 p-2 rounded-xl bg-slate-50 border border-slate-100">
          <label className="flex items-center gap-2 text-xs text-slate-600 mb-2">
            <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)}
              data-testid={`duty-${dg.key}-override`} />
            Override gender filter (show all active ushers)
          </label>
          <div className="max-h-44 overflow-y-auto space-y-1">
            {eligible.length === 0 && <p className="text-xs text-slate-500 py-2">No eligible ushers.</p>}
            {eligible.map(t => {
              const picked = assigned.includes(t.id);
              return (
                <button key={t.id} onClick={() => onToggle(t.id)}
                  data-testid={`duty-${dg.key}-pick-${t.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-xs text-left transition-colors ${
                    picked ? 'bg-violet-100 text-violet-800 font-semibold' : 'bg-white hover:bg-slate-100 text-slate-700'}`}>
                  <span className="truncate">{t.full_name}</span>
                  {picked ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <Plus className="w-3.5 h-3.5 shrink-0 text-slate-400" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsherRotaSection;


// =======================================================================
// UsherTeamRow — roster card with per-member eligible-duties picker
// =======================================================================
const UsherTeamRow = ({ entry, onToggleActive, onRemove, onSaveEligibleDuties }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.eligible_duties || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(entry.eligible_duties || []); }, [entry.eligible_duties]);

  // Duties that match this member's gender (greyed-out otherwise)
  const duties = useMemo(() => {
    const g = (entry.gender || '').toLowerCase();
    return DUTY_GROUPS.map(dg => ({
      ...dg,
      genderFit: dg.gender === 'Any' || dg.gender.toLowerCase() === g,
    }));
  }, [entry.gender]);

  const toggleDuty = (key) => {
    setDraft(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
  };

  const save = async () => {
    setSaving(true);
    await onSaveEligibleDuties(draft);
    setSaving(false);
    setEditing(false);
  };

  const summary = (entry.eligible_duties || []).length === 0
    ? 'All eligible duties'
    : `${entry.eligible_duties.length} selected`;

  return (
    <div data-testid={`usher-team-row-${entry.id}`}
      className={`p-4 rounded-2xl border transition-all ${
        entry.active ? 'bg-white border-slate-100' : 'bg-slate-50 border-slate-200 opacity-70'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800 truncate">{entry.full_name || '(unknown)'}</p>
          <p className="text-xs text-slate-500 truncate">
            {entry.member_number || '—'} · {entry.gender || '—'}{entry.phone ? ` · ${entry.phone}` : ''}
          </p>
          <p className="text-xs text-violet-700 font-medium mt-1">Duties: {summary}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onToggleActive} data-testid={`usher-toggle-${entry.id}`}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
              entry.active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                           : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
            {entry.active ? 'Active' : 'Inactive'}
          </button>
          <button onClick={() => setEditing(v => !v)}
            data-testid={`usher-duties-edit-${entry.id}`}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-violet-100 text-violet-700 hover:bg-violet-200">
            {editing ? 'Close' : 'Duties'}
          </button>
          <button onClick={onRemove} data-testid={`usher-remove-${entry.id}`}
            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 p-3 rounded-xl bg-violet-50 border border-violet-100">
          <p className="text-xs font-semibold text-violet-800 mb-2">
            Tick the duties this usher is able to do. Leave ALL unticked to allow every duty (subject to gender rules).
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {duties.map(dg => {
              const on = draft.includes(dg.key);
              return (
                <button key={dg.key} onClick={() => toggleDuty(dg.key)}
                  data-testid={`usher-duty-${entry.id}-${dg.key}`}
                  disabled={!dg.genderFit}
                  title={dg.genderFit ? '' : `Gender rule: ${dg.gender} only`}
                  className={`text-left px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                    !dg.genderFit
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : on
                        ? 'bg-violet-600 text-white shadow'
                        : 'bg-white text-slate-700 hover:bg-violet-100'
                  }`}>
                  {on ? <CheckCircle2 className="w-3.5 h-3.5"/> : <Plus className="w-3.5 h-3.5"/>}
                  {dg.label}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setDraft([]); }} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-200 text-slate-700 hover:bg-slate-300">
              Clear (allow all)
            </button>
            <button onClick={save} disabled={saving} data-testid={`usher-duties-save-${entry.id}`}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-violet-950 hover:bg-amber-600 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
